/**
 * The navigation menu — CLAUDE.md §4.12, and the only place a report enters it.
 *
 * Two levels. The top is the **area of the business**, a fixed set that never grows with the
 * report count; the second is a **stage** inside an area that genuinely divides. Never grouped by
 * legacy module name, source system, factory, or porting order — porting order is a delivery
 * concern and must not leak into navigation, so a report is filed under its area on first deploy.
 */

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBoxesPacking,
  faBoxesStacked,
  faClipboardCheck,
  faFileInvoice,
  faIndustry,
  faListCheck,
  faScissors,
  faShirt,
  faStamp,
  faTruckFast,
  faUserClock,
  faUsersGear,
  faWarehouse,
} from '@fortawesome/free-solid-svg-icons';

/** A single report in the menu. */
export interface MenuItem {
  /** The URL segment, the API's `{reportId}`, and the server's `reports/` folder name — one value. */
  reportId: string;
  /** What floor users call it — not what the database spells it (§4.10). */
  label: string;
  /** Names this report used to go by, from the `replaces:` block of its `report.yaml` (§5.3). */
  aliases?: readonly string[];
  /**
   * Base Portal roles that see this item, mirroring `visibility:`. Advisory only — access is
   * enforced server-side against the verified token (§6.7). Omitted means every signed-in user.
   */
  roles?: readonly string[];
}

export type MenuAreaId =
  | 'order-management'
  | 'material-planning'
  | 'raw-material'
  | 'production-control'
  | 'quality-assurance'
  | 'delivery-performance'
  | 'hr-admin';

export type MenuGroupId =
  | 'cutting'
  | 'sop'
  | 'supermarket'
  | 'sewing'
  | 'finished-goods'
  | 'attendance';

/** A stage inside an area. Only areas that genuinely have stages declare any. */
export interface MenuGroup {
  id: MenuGroupId;
  /** The stage, named as the floor names it. */
  label: string;
  /** One line for the overview card — what this stage covers. */
  description: string;
  icon: IconDefinition;
  /** Other names this stage goes by. A renamed stage keeps its old name or the rename is a
   *  findability regression for the people who have used it longest (§4.12). */
  aliases?: readonly string[];
  items: readonly MenuItem[];
}

export interface MenuArea {
  id: MenuAreaId;
  /** The area, named as the business names it. */
  label: string;
  /** One line for the overview card — what this area covers. */
  description: string;
  icon: IconDefinition;
  /** Other names this area goes by — same job as `MenuGroup.aliases`. */
  aliases?: readonly string[];
  /** Reports filed directly under the area — where it declares no stages, which is most of them. */
  items: readonly MenuItem[];
  /** Stages inside this area. Empty where the area has none. */
  groups: readonly MenuGroup[];
  /**
   * True where the stages are a sequence work physically passes through, so the menu draws them as
   * a route rather than a list. Production Control is the only one; reordering edits that array.
   */
  sequential?: boolean;
}

/** The areas and their stages. Areas stay listed while empty, so the shape is legible before full. */
export const MENU_AREAS: readonly MenuArea[] = [
  {
    id: 'order-management',
    label: 'Order Management',
    description: 'Orders on the books and how they are progressing.',
    icon: faFileInvoice,
    items: [],
    groups: [],
  },
  {
    id: 'material-planning',
    label: 'Material Planning',
    description: 'What material is needed, when, and whether it will arrive in time.',
    icon: faListCheck,
    items: [],
    groups: [],
  },
  {
    id: 'raw-material',
    label: 'Raw Material',
    description: 'Fabric and trims received, held in store, and issued to production.',
    icon: faBoxesStacked,
    items: [
      {
        reportId: 'bulk-trim-card',
        label: 'Bulk Trim Card',
      },
      {
        // The overview comes first: it answers "is the order book healthy, and where is it not?",
        // and a reader who needs the detail behind a panel goes on to the report below. Both are
        // new reports rather than ports, so neither carries `aliases` — there is no legacy name for
        // a search to resolve.
        reportId: 't2-business-dashboard',
        label: 'T2 Business Dashboard',
      },
      {
        // The same population, listed row by row from the same view. Supplier delivery performance
        // is this report's "Performance - SDP" view rather than an entry of its own; a view has no
        // menu identity, and the reader who wants it is already on this report.
        reportId: 't2-business',
        label: 'T2 Business Report',
      },
    ],
    groups: [],
  },
  {
    id: 'production-control',
    label: 'Production Control',
    description: 'The floor, stage by stage, from cutting through to finished goods.',
    icon: faIndustry,
    items: [],
    sequential: true,
    groups: [
      {
        id: 'cutting',
        label: 'Cutting',
        description: 'Cut plans, output, and cutting-room performance.',
        icon: faScissors,
        items: [
          {
            // One report for the whole legacy spreading family. The detail, summary and by-length
            // views join it in later pairs; all four legacy names are aliased from the start so a
            // search for any of them lands here (§4.12 — the menu is by operation, not by module).
            reportId: 'spreading',
            label: 'Spreading',
            aliases: [
              'SFTS Report > Spreading By Part',
              'SFTS Report > Spreading Detail Report',
              'SFTS Report > Spreading Summary Report',
              'SFTS Report > Spreading Detail By Length',
            ],
          },
        ],
      },
      {
        id: 'sop',
        label: 'SOP',
        description:
          'Printing, heat transfer, embroidery, and bonding on cut panels before they reach the line.',
        icon: faStamp,
        // Legacy called this "2nd Operation" and placed it after sewing; the old name stays
        // searchable because that is what its users still call it.
        aliases: ['2nd Operation', 'Second Operation', 'Printing', 'Heat Transfer', 'Embroidery', 'Bonding'],
        items: [
          {
            // One report for the summary and detail views of the legacy 2nd Operation
            // family. The two MI MES variants are not ported yet, so their names are deliberately
            // absent from the aliases: a search for one should not claim to land on it.
            reportId: 'second-operation',
            label: '2nd Operation',
            aliases: [
              'SFTS Report > 2nd Operation Summary Report',
              'SFTS Report > 2nd Operation Detail Report',
            ],
          },
        ],
      },
      {
        id: 'supermarket',
        label: 'Supermarket',
        description: 'What is waiting, where, and for how long.',
        icon: faWarehouse,
        items: [],
      },
      {
        id: 'sewing',
        label: 'Sewing',
        description: 'Line output, efficiency, and hourly production.',
        icon: faShirt,
        items: [
          {
            reportId: 'production-monthly-output',
            label: 'Production Monthly Output',
            aliases: ['Production Report > Production Monthly Output'],
          },
          {
            reportId: 'sewing-efficiency',
            label: 'Sewing Efficiency',
            aliases: [
              'Win Dashboard Detail > Efficiency and Output Dashboard',
              'Win Dashboard Detail > Efficiency Management',
              'Win Dashboard Detail > Efficiency Detail',
            ],
          },
          {
            // Above the floor board: the factory board is the wider scope and drills into it.
            // No aliases — it replaces no legacy report (see its report.yaml).
            reportId: 'sewing-factory-board',
            label: 'Sewing Factory Board',
          },
          {
            reportId: 'sewing-floor-board',
            label: 'Sewing Floor Board',
            aliases: ['Production Report > Efficiency & WFT', 'Efficiency and WFT', 'WFT'],
          },
          {
            reportId: 'sewing-hourly-board',
            label: 'Sewing Hourly Board',
            aliases: [
              'Hourly Production Monitoring',
              'Production Report > Hourly Production Monitoring',
            ],
          },
        ],
      },
      {
        id: 'finished-goods',
        label: 'Finished Goods',
        description: 'Garments finished, packed, and ready to ship.',
        icon: faBoxesPacking,
        items: [],
      },
    ],
  },
  {
    id: 'quality-assurance',
    label: 'Quality Assurance',
    description: 'Defects, audits, and rework across the floor.',
    icon: faClipboardCheck,
    items: [],
    groups: [],
  },
  {
    id: 'delivery-performance',
    label: 'Delivery Performance',
    description: 'Orders shipped and orders still owed.',
    icon: faTruckFast,
    items: [],
    groups: [],
  },
  {
    id: 'hr-admin',
    label: 'HR & Admin',
    description: 'People on the floor, and the administration around them.',
    icon: faUsersGear,
    items: [],
    groups: [
      {
        id: 'attendance',
        label: 'Attendance',
        description: 'Who is on the floor, across every operation.',
        icon: faUserClock,
        items: [],
      },
    ],
  },
];

/** The route for a report. The one place this path is spelled out. */
export function reportPath(reportId: string): string {
  return `/reports/${encodeURIComponent(reportId)}`;
}

/**
 * The menu as these roles see it. Emptied areas and stages are kept rather than dropped, so
 * positions do not shuffle between two users comparing notes.
 */
export function menuForRoles(roles: readonly string[]): readonly MenuArea[] {
  const permitted = (items: readonly MenuItem[]) =>
    items.filter(
      (item) => item.roles === undefined || item.roles.some((role) => roles.includes(role)),
    );

  return MENU_AREAS.map((area) => ({
    ...area,
    items: permitted(area.items),
    groups: area.groups.map((group) => ({ ...group, items: permitted(group.items) })),
  }));
}

/** Every report in an area, whether filed directly or under one of its stages. */
export function areaItems(area: MenuArea): readonly MenuItem[] {
  return [...area.items, ...area.groups.flatMap((group) => group.items)];
}

export function countMenuItems(areas: readonly MenuArea[]): number {
  return areas.reduce((total, area) => total + areaItems(area).length, 0);
}

/** A report and where it sits, for pages that only know an id. */
export interface MenuLocation {
  area: MenuArea;
  /** The stage it is filed under, or null when it sits directly in the area. */
  group: MenuGroup | null;
  item: MenuItem;
}

export function findMenuItem(
  areas: readonly MenuArea[],
  reportId: string,
): MenuLocation | null {
  for (const area of areas) {
    const direct = area.items.find((candidate) => candidate.reportId === reportId);
    if (direct !== undefined) {
      return { area, group: null, item: direct };
    }
    for (const group of area.groups) {
      const item = group.items.find((candidate) => candidate.reportId === reportId);
      if (item !== undefined) {
        return { area, group, item };
      }
    }
  }
  return null;
}

/**
 * Filter the menu by a typed term, matching a report's label and id, its area and stage, and every
 * alias of each. Areas and stages with no match drop out — while searching, sequence is not the point.
 */
export function searchMenu(areas: readonly MenuArea[], term: string): readonly MenuArea[] {
  const needle = term.trim().toLowerCase();
  if (needle === '') {
    return areas;
  }

  return areas
    .map((area) => {
      const areaNames = [area.label, ...(area.aliases ?? [])];
      return {
        ...area,
        items: area.items.filter((item) => matches(item, areaNames, needle)),
        groups: area.groups
          .map((group) => ({
            ...group,
            items: group.items.filter((item) =>
              matches(item, [...areaNames, group.label, ...(group.aliases ?? [])], needle),
            ),
          }))
          .filter((group) => group.items.length > 0),
      };
    })
    .filter((area) => area.items.length > 0 || area.groups.length > 0);
}

function matches(item: MenuItem, context: readonly string[], needle: string): boolean {
  const haystack = [item.label, item.reportId, ...context, ...(item.aliases ?? [])];
  return haystack.some((value) => value.toLowerCase().includes(needle));
}
